import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  Paper,
  Tooltip,
  Stack,
  Typography,
  TableContainer,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { isEmpty } from "lodash";
import { DataContext } from "../../../context/DataContext";
import { getRuta, isAccess, simpleMessage } from "../../../helpers/Helpers";
import { getToken } from "../../../api/Account";
import NoData from "../../../components/NoData";
import { getDepartmentByIdAsync } from "../../../api/Locations";
import ListFooter from "../../../components/ui/ListFooter";
import BackButton from "../../../components/ui/BackButton";
import SmallModal from "../../../components/modals/SmallModal";
import AddEditAbMun from "./AddEditAbMun";
import { getTokens } from "../../../theme";

const Municipalities = () => {
  let ruta = getRuta();
  const { setIsLoading, reload, access, setIsUnautorized, isDarkMode } =
    useContext(DataContext);
  const t = getTokens(isDarkMode);
  const token = getToken();
  let navigate = useNavigate();
  const { id } = useParams();

  const [department, setDepartment] = useState([]);
  const [municipalityList, setMunicipalityList] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsperPage] = useState(10);
  const indexLast = currentPage * itemsperPage;
  const indexFirst = indexLast - itemsperPage;
  const currentItem = municipalityList.slice(indexFirst, indexLast);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const [showModal, setShowModal] = useState(false);
  const [selectedMun, setSelectedMun] = useState("");

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);

      const resultDepto = await getDepartmentByIdAsync(token, id);
      if (!resultDepto.statusResponse) {
        setIsLoading(false);
        if (resultDepto.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(resultDepto.error, "error");
        return;
      }

      setDepartment(resultDepto.data.result);
      setMunicipalityList(resultDepto.data.result.municipalities);
      setIsLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const actionButtonSx = {
    width: 32,
    height: 32,
    borderRadius: "9px",
    border: `1px solid ${t.fieldLine}`,
    backgroundColor: t.field,
  };

  return (
    <div>
      <Paper sx={{ overflow: "hidden" }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ padding: "18px 22px", borderBottom: "1px solid", borderColor: "divider" }}
        >
          <BackButton onClick={() => navigate(`${ruta}/locations`)} />

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                fontSize: 16.5,
                color: "text.primary",
                lineHeight: 1.1,
              }}
            >
              {department.name}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.disabled", marginTop: "3px" }}>
              Municipios del departamento
            </Typography>
          </Box>
        </Stack>

        {isEmpty(currentItem) ? (
          <NoData />
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">#</TableCell>
                    <TableCell>Municipio</TableCell>
                    <TableCell align="center">Abreviatura</TableCell>
                    <TableCell align="center">Comunidades</TableCell>
                    <TableCell align="center">Ver</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {currentItem.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => navigate(`${ruta}/locations/municipalities/${row.id}`)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell align="center" sx={{ color: "text.disabled", fontWeight: 600 }}>
                        {row.id}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>{row.name}</TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                          <Box component="span" sx={{ color: "text.secondary" }}>
                            {row.abreviatura}
                          </Box>
                          {isAccess(access, "CONFIG UPDATE") ? (
                            <Tooltip title={row.abreviatura === "" ? "Agregar" : "Editar"}>
                              <IconButton
                                size="small"
                                sx={{
                                  ...actionButtonSx,
                                  color: row.abreviatura === "" ? "primary.main" : "warning.main",
                                }}
                                onClick={() => {
                                  setSelectedMun(row);
                                  setShowModal(true);
                                }}
                              >
                                {row.abreviatura === "" ? (
                                  <AddRoundedIcon sx={{ fontSize: 15 }} />
                                ) : (
                                  <EditRoundedIcon sx={{ fontSize: 15 }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell align="center" sx={{ color: "text.secondary" }}>
                        {row.communities.length}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Ver comunidades">
                          <IconButton
                            size="small"
                            sx={{ ...actionButtonSx, color: "primary.main" }}
                            onClick={() => navigate(`${ruta}/locations/municipalities/${row.id}`)}
                          >
                            <VisibilityRoundedIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <ListFooter
              data={municipalityList}
              currentCount={currentItem.length}
              indexFirst={indexFirst}
              paginate={paginate}
              itemsperPage={itemsperPage}
              noun="municipios"
            />
          </>
        )}
      </Paper>

      <SmallModal
        titulo={
          selectedMun.abreviatura === ""
            ? "Agregar Abreviatura"
            : "Editar Abreviatura"
        }
        isVisible={showModal}
        setVisible={setShowModal}
      >
        <AddEditAbMun selectedMun={selectedMun} setShowModal={setShowModal} />
      </SmallModal>
    </div>
  );
};

export default Municipalities;
